import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  Breadcrumbs,
  useTheme,
  css,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import PaymentFormOnboarding from '../PaymentFormOnboarding/PaymentFormOnboarding';
import { staticFileLocation } from '@server/constants';
import { darkBlue, darkBlue2 } from '@styles/colors';
import { bold } from '@styles/fontWeights';
import SelectPlanModal from './SelectPlanModal';
import ReqMoreInfoModal from './ReqMoreInfoModal';
import { Star } from '@mui/icons-material';
import dayjs from 'dayjs';
import PlanName from './PlanName';
import { getCookie } from '@utils/getCookie';
import { PLANS } from '@utils/pricing';

type PlanFeature = string;
type BillingCycle = 'Monthly' | 'Bi-Annual' | 'Annual';
type PlanType = 'starter' | 'smarter' | 'pro';

type PlanOption = {
  billingCycle: BillingCycle;
  price: string;
  actualPrice?: string;
};

type Plan = {
  planName: string;
  showBillingCycle: boolean;
  price?: string;
  mainHeading: string;
  buttonText: string;
  subheading1: string;
  upgrationText: string;
  upgrationBtnText: string;
  subheading2: string;
  planHeading: string;
  disclaimer: string;
  planFeatures: PlanFeature[];
  plans?: PlanOption[];
};

type PlansData = {
  starter: Plan;
  smarter: Plan;
  pro: Plan;
};

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);
type PickPlanStepProps = {
  handleNext: () => void;
};

const PickPlanStep: React.FC<PickPlanStepProps> = ({ handleNext }) => {
  let today = dayjs();
  let date = dayjs().add(7, 'day');
  let datePlus7Days = today.add(7, 'day').format('MMMM DD, YYYY');
  let datePlus1Months = today.add(1, 'month').format('MMMM DD, YYYY');
  let datePlus6Months = today.add(182, 'day').format('MMMM DD, YYYY');
  let datePlus12Months = today.add(350, 'day').format('MMMM DD, YYYY');
  const signup_cycle = getCookie('signup_cycle');
  const signup_plan = getCookie('signup_plan');
  const theme = useTheme();
  const giveTrail = getCookie('ref_singup_code');
  const [currentPlan, setCurrentPlan] = useState<PlanType>(
    (signup_plan as PlanType) || 'starter',
  );
  const [selectedBillingCycle, setSelectedBillingCycle] =
    useState<BillingCycle>((signup_cycle as BillingCycle) || 'Annual');
  const [actualPrice, setActualPrice] = useState<number>(0);
  const [showPlanSelectionModal, setShowPlanSelectionModal] =
    useState<boolean>(false);
  const [showReqInfoModal, setShowReqInfoModal] = useState<boolean>(false);
  const billingCycleText = () => {
      return '/ mo';
  };
  const calclulatedPrice = () => {
    if (selectedBillingCycle === 'Monthly') {
      return actualPrice;
    }
    if (selectedBillingCycle === 'Bi-Annual' && actualPrice) {
      return Number(actualPrice) * 6;
    }
    if (selectedBillingCycle === 'Annual' && actualPrice) {
      return Number(actualPrice) * 12;
    }
  };


  const plansData: PlansData = {
    starter: {
      planName: 'Starter',
      showBillingCycle: false,
      price: giveTrail ? '0' : PLANS.STARTER.MONTHLY_PRICE,
      mainHeading: 'Thats it!',
      buttonText: 'Post My Job Now!',
      subheading1: 'Great hires on the way',
      upgrationText: 'Need to post more jobs?',
      upgrationBtnText: 'Upgrade Now',
      subheading2:
        giveTrail ? 'Enter payment & your first job goes live for free for the first 7 days!  Cancel anytime - we’re always here to help no matter what you need.' :  'Enter payment & your first job goes live for just $1! We’re always here to help along the way no matter what you need',
      planHeading: 'For the first Week, $20 / mo after',
      disclaimer: `*Plan will renew automatically for $20 / mo  unless cancelled prior to ${datePlus7Days} <br/>*Subscription cancellations require 15 days notice prior to your next billing cycle`,
      planFeatures: [
        'One job post',
        'No contract - cancel any time',
        'Post jobs to all major job boards',
        'Attract, engage, & interview seamlessly',
        'Pre-screening & applicant tagging',
        'Customized, branded careers page',
        'Social & web integration',
        'Unlimited applications & hires',
      ],
    },
    smarter: {
      planName: 'Smarter',
      subheading1: 'Great hires on the way',
      subheading2:
        'Enter payment & your first job goes live immediately. Add more jobs, customize your application, & set up automation in your account.',
      disclaimer: `*Plan bills today for $${calclulatedPrice()}${selectedBillingCycle === 'Monthly' ? '/ mo' : selectedBillingCycle === 'Bi-Annual' ? ' bi-annually' : selectedBillingCycle === 'Annual' ? '/ yr' : ''} & will renew ${selectedBillingCycle === 'Monthly'
          ? 'automatically'
          : `unless cancelled on the ${selectedBillingCycle === 'Bi-Annual'
              ? `${datePlus6Months} month`
              : selectedBillingCycle === 'Annual'
                ? `${datePlus12Months}`
                : ''
          }`
        } <br/> *Subscription cancellations require 15 days notice prior to your next billing cycle`,

      showBillingCycle: true,
      upgrationText: 'Need new hire onboarding?',
      upgrationBtnText: 'Upgrade Now',
      plans: [
        { billingCycle: 'Monthly', price: PLANS.SMARTER.MONTHLY_PRICE },
        { billingCycle: 'Bi-Annual', price: PLANS.SMARTER.BIANNUAL_PRICE,actualPrice: PLANS.SMARTER.DISCOUNT_BIANNUAL },
        { billingCycle: 'Annual', price: PLANS.SMARTER.ANNUAL_PRICE, actualPrice: PLANS.SMARTER.DISCOUNT_ANNUAL },
      ],
      mainHeading: 'Nice work!',
      buttonText: 'Start Hiring Now',
      planHeading: 'Everything in Starter +',
      planFeatures: [
        'Unlimited job posts (go crazy)',
        'Full cycle hiring automation',
        'Hiring analytics',
        'Custom branded QR & hiring graphics',
        'One on one account onboarding',
        'Unlimited support',
      ],
    },
    pro: {
      planName: 'Pro',
      showBillingCycle: true,
      subheading1: 'Great hires on the way',
      upgrationText: 'More than 1 location?',
      upgrationBtnText: 'Request Info',
      subheading2:
        'Enter payment & your first job goes live immediately. Add jobs, customize your application, & set up onboarding & automation in your account.',
      disclaimer: `*Plan bills today for $${calclulatedPrice()} ${selectedBillingCycle === 'Monthly' ? '/ mo' : selectedBillingCycle === 'Bi-Annual' ? ' bi-annually' : selectedBillingCycle === 'Annual' ? '/ yr' : ''} & will renew ${selectedBillingCycle === 'Monthly'
          ? 'automatically'
          : `unless cancelled on the ${selectedBillingCycle === 'Bi-Annual'
            ? `${datePlus6Months} month`
            : selectedBillingCycle === 'Annual'
              ? `${datePlus12Months}`
              : ''
          }`
        } <br/> *Subscription cancellations require 15 days notice prior to your next billing cycle`,
      plans: [
        { billingCycle: 'Monthly', price: PLANS.PRO.MONTHLY_PRICE },
        { billingCycle: 'Bi-Annual', price: PLANS.PRO.BIANNUAL_PRICE, actualPrice: PLANS.PRO.DISCOUNT_BIANNUAL },
        { billingCycle: 'Annual', price: PLANS.PRO.ANNUAL_PRICE, actualPrice: PLANS.PRO.DISCOUNT_ANNUAL },
      ],
      mainHeading: 'Nice work!',
      buttonText: 'Start Hiring Now',
      planHeading: 'Everything in Smarter +',
      planFeatures: [
        'Digital new hire onboarding',
        'Customized company paperwork',
        'Hiring document storage',
        'Teams managemenet',
        'Multi-user access',
        'Manager permissions & controls',
      ],
    },
  };

  const hanldeSelectPlanModal = () => {
    setShowPlanSelectionModal(!showPlanSelectionModal);
  };
  const hanldeReqModal = () => {
    setShowReqInfoModal(!showReqInfoModal);
  };

  const upgradePlan = () => {
    if (currentPlan === 'starter') hanldeSelectPlanModal();
    else if (currentPlan === 'smarter') setCurrentPlan('pro');
    else if (currentPlan === 'pro') hanldeReqModal();
  };
  const handleSelectPlan = (plan: PlanType) => {
    setCurrentPlan(plan);
    setShowPlanSelectionModal(false);
  };
  useEffect(() => {
    if (plansData[currentPlan].price)
      setActualPrice(Number(plansData[currentPlan].price));
    else if (
      plansData[currentPlan].plans?.find(
        (p) => p.billingCycle === selectedBillingCycle,
      )?.price
    ) {
      setActualPrice(
        Number(
          plansData[currentPlan].plans?.find(
            (p) => p.billingCycle === selectedBillingCycle,
          )?.price,
        ),
      );
    }
  }, [selectedBillingCycle, currentPlan]);


  const hanldeBillingCycle = (billingCycle: BillingCycle) => {
    setSelectedBillingCycle(billingCycle);
  };

  const discountedPrice = plansData[currentPlan].plans?.find(
    (p) => p.billingCycle === selectedBillingCycle,
  )?.actualPrice;

  return (
    <>
      <Box p={4} boxShadow={3} borderRadius={2} display="flex" alignItems='center' gap={5}>
        <Box
          flex={1}
          pr={2}
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <img
            src={`${staticFileLocation}img/logos/LogoStampShadow.png`}
            css={css`
              max-width: 50px;
              width: 100%;
              margin-right: ${theme.spacing(2)};
              margin-top: -20px;
            `}
            alt="Notifications"
          />
          <Typography variant="h1" fontWeight="bold">
            {plansData[currentPlan].mainHeading}
          </Typography>
          <Typography variant="h4" fontSize='24px' color={darkBlue2} fontWeight={bold}>
            {plansData[currentPlan].subheading1}
          </Typography>
          <Typography
            color={darkBlue}
            fontWeight={bold}
            textAlign="center"
            mt={2}
            lineHeight={1.4}
            variant="h6"
            sx={{ maxWidth: (currentPlan === 'starter') && giveTrail ? '300px':  currentPlan === 'starter' ? '330px' : currentPlan === 'pro'?'370px': '352px'}}
          >
            {plansData[currentPlan].subheading2}
          </Typography>
          <Elements stripe={stripePromise}>
            <PaymentFormOnboarding
              selectedPlan={currentPlan}
              paymentPeriod={selectedBillingCycle}
              handleClose={handleNext}
              submitButtonText={plansData[currentPlan].buttonText}
            />
          </Elements>
          <Typography
            variant="caption"
            textAlign="center"
            mt={1}
            component="div"
            color="gray"
            sx={{ maxWidth: currentPlan === 'starter' ? '240px' : selectedBillingCycle === 'Monthly' ? '310px': '300px', fontStyle: 'italic', fontSize: '0.8rem' }}
            dangerouslySetInnerHTML={{
              __html: plansData[currentPlan].disclaimer,
            }}
          />
          <Typography variant="h4" mt={2} color={darkBlue2} fontWeight={bold}>
            {plansData[currentPlan].upgrationText}
          </Typography>
          <Button
            variant="outlined"
            onClick={upgradePlan}
            sx={{
              paddingX: 5,
              paddingY: 1,
              fontWeight: 'bold',
              mt: 2,
              textTransform: 'none',
              position: 'relative',
            }}
          >
            {plansData[currentPlan].upgrationBtnText}
            <Box
              sx={{
                position: 'absolute',
                top: '-5px',
                right: '-5px',
                backgroundColor: 'primary.main',
                color: 'white',
                borderRadius: '50%',
                width: 18,
                height: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 2,
              }}
            >
              <Star sx={{ fontSize: 12 }} />
            </Box>
          </Button>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box
          flex={1}
          pl={2}
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
        >
          <Typography variant="h3" fontWeight="bold" textAlign="center">
            Your Selected Plan
          </Typography>
          {plansData[currentPlan].showBillingCycle && (
            <Breadcrumbs
              separator="/"
              aria-label="breadcrumb"
              sx={{
                // border: '1px solid gray',
                // borderRadius: '50px',
                alignItems: 'center',
                width: 'fit-content',
                // padding: '0 30px',
              }}
            >
              {plansData[currentPlan].plans?.map(({ billingCycle }) => (
                <Button
                  key={billingCycle}
                  color={
                    selectedBillingCycle === billingCycle
                      ? 'primary'
                      : 'inherit'
                  }
                  onClick={() => setSelectedBillingCycle(billingCycle)}
                  variant="contained"
                  size='small'
                >
                  {billingCycle}
                  {selectedBillingCycle === billingCycle && (
                    <span
                      style={{
                        position: 'absolute',
                        bottom: '-5px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: '#ff5104',
                      }}
                    />
                  )}
                </Button>
              ))}
            </Breadcrumbs>
          )}
          <PlanName planName={plansData[currentPlan].planName} />
          <Box display="flex" alignItems="center" gap='10px'>
            <Typography fontSize="50px" variant="h1" fontWeight="bold">
              ${actualPrice}
            </Typography>
            {discountedPrice && (
              <Chip
                label={`Save ${discountedPrice}%`}
                sx={{
                  backgroundColor: "#FF5104",
                  color: "#FFFFFF",
                  fontSize: "12px",
                  height: "20px",
                  borderRadius: "4px",
                }}
              />
            )}
          </Box>
          {currentPlan !== 'starter' && (
            <Typography fontSize='18px' color="gray">
              {billingCycleText()} billed <b>${calclulatedPrice()}</b>{' '}
              {selectedBillingCycle}
              {!selectedBillingCycle?.includes('ly') && 'ly'}
            </Typography>
          )}

          <Typography variant="h6" color="gray" fontWeight={1000} fontSize='18px'>
            {plansData[currentPlan].planHeading}
          </Typography>

          <Box mt={2}>
            {plansData[currentPlan].planFeatures.map((feature, index) => (
              <Box
                margin={2}
                display="flex"
                alignItems="center"
                key={index}
                gap={3}
              >
                <CheckCircleIcon color="primary" />
                <Typography color="gray">{feature}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      {showPlanSelectionModal && (
        <SelectPlanModal
          open={showPlanSelectionModal}
          handleClose={hanldeSelectPlanModal}
          handleSelectPlan={handleSelectPlan}
          hanldeBillingCycle={hanldeBillingCycle}
          selectedBillingCycle={selectedBillingCycle}
        />
      )}
      {showReqInfoModal && (
        <ReqMoreInfoModal
          open={showReqInfoModal}
          handleClose={hanldeReqModal}
        />
      )}
    </>
  );
};

export default PickPlanStep;
